import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteSerieLangModal,
  SERIE_LANG_PAGE_SIZE,
  SerieLangTable,
  useDeleteSerieLang,
  useSerieLangList,
} from '@/features/series-lang-management';
import { useSerie } from '@/features/series-management';
import type { SerieLangViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/series/$serieId/langs/')({
  component: SerieLangListPage,
});

/** Página de listagem de Idiomas da Série. */
export function SerieLangListPage() {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { serieId: serieIdParam } = Route.useParams();
  const serieId = Number(serieIdParam);

  const { serie } = useSerie(serieId);
  const [page, setPage] = useState(1);
  const { langs, totalCount, totalPages, currentPage, isLoading, isError, refetch } =
    useSerieLangList(serieId, page);
  const deleteSerieLang = useDeleteSerieLang(serieId);

  const [deleting, setDeleting] = useState<SerieLangViewModel | null>(null);

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteSerieLang.mutate(
      { serieId, id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('langs.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('langs.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button
      hierarchy="primary"
      size="lg"
      leadingIcon={PlusIcon}
      onClick={() =>
        void navigate({ to: '/series/$serieId/langs/create', params: { serieId: serieIdParam } })
      }
    >
      {t('langs.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('langs.title')}
        subtitle={
          serie?.title
            ? t('langs.subtitle', { serie: serie.title, count: totalCount })
            : t('langs.subtitleNoSerie', { count: totalCount })
        }
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/series">{t('langs.breadcrumb.series')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('langs.breadcrumb.langs')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('langs.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('langs.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <SerieLangTable
          data={langs}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={SERIE_LANG_PAGE_SIZE}
          onPageChange={setPage}
          emptyStateAction={addButton}
          onEdit={lang =>
            void navigate({
              to: '/series/$serieId/langs/$langId',
              params: { serieId: serieIdParam, langId: String(lang.id) },
            })
          }
          onDelete={setDeleting}
        />
      )}

      <DeleteSerieLangModal
        open={deleting !== null}
        langTitle={deleting?.title ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
